import React from 'react';
import { FiArrowRight, FiCheckCircle, FiClock, FiFileText } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import useDocumentTitle from '../../../hooks/useDocumentTitle';
import {
  localizeSurveyText,
  SurveySummary,
  useSurveys,
} from '../../../helpers/api/surveys/surveys';
import { ErrorDisplay } from '../../ComponnentsUI/common/ErrorDisplay';
import { LoadingSpinner } from '../../ComponnentsUI/common/LoadingSpinner';

function formatDate(value: string | null | undefined): string {
  if (!value) {
    return '-';
  }
  return new Date(value).toLocaleDateString();
}

const SurveyCard: React.FC<{
  survey: SurveySummary;
  language: string;
  onOpen: () => void;
  openLabel: string;
  statusLabel: string;
  metaLabel: string;
}> = ({ survey, language, onOpen, openLabel, statusLabel, metaLabel }) => {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${survey.is_due ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200' : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200'}`}>
              {survey.is_due ? <FiClock size={12} /> : <FiCheckCircle size={12} />}
              {statusLabel}
            </span>
            <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 dark:text-gray-400">
              <FiFileText size={12} />
              {metaLabel}
            </span>
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {localizeSurveyText(survey.title, language)}
            </h2>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
              {localizeSurveyText(survey.description, language)}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onOpen}
          className="inline-flex items-center justify-center gap-2 rounded-full bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700"
        >
          {openLabel}
          <FiArrowRight size={14} />
        </button>
      </div>
    </div>
  );
};

const SurveysPage: React.FC = () => {
  const { t, i18n } = useTranslation('Surveys');
  useDocumentTitle(t('documentTitle'));
  const navigate = useNavigate();
  const { data, isLoading, error } = useSurveys(false);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <ErrorDisplay message={t('loadError')} />;
  }

  const surveys = data ?? [];
  const dueSurveys = surveys.filter((survey) => survey.is_due);
  const completedSurveys = surveys.filter((survey) => !survey.is_due);

  return (
    <div className="bg-card-color-200 dark:bg-gray-900 p-4 sm:p-6 lg:p-8">
      <div className="mx-auto max-w-5xl space-y-8">
        <div className="rounded-3xl bg-white/95 p-6 shadow-sm ring-1 ring-gray-200 dark:bg-gray-800 dark:ring-gray-700">
          <p className="text-sm font-medium text-blue-600 dark:text-blue-300">{t('eyebrow')}</p>
          <h1 className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{t('title')}</h1>
          <p className="mt-2 max-w-2xl text-sm text-gray-600 dark:text-gray-300">{t('subtitle')}</p>
          <div className="mt-4 flex flex-wrap gap-3 text-sm text-gray-500 dark:text-gray-400">
            <span>{t('pendingCount', { count: dueSurveys.length })}</span>
            <span>{t('activeCount', { count: surveys.length })}</span>
          </div>
        </div>

        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{t('pendingTitle')}</h2>
              <p className="text-sm text-gray-600 dark:text-gray-300">{t('pendingDescription')}</p>
            </div>
          </div>
          {dueSurveys.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-6 text-sm text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
              <p className="font-semibold text-gray-900 dark:text-white">{t('allCaughtUpTitle')}</p>
              <p className="mt-1">{t('allCaughtUpDescription')}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {dueSurveys.map((survey) => (
                <SurveyCard
                  key={survey.id}
                  survey={survey}
                  language={i18n.language}
                  onOpen={() => navigate(`/surveys/${survey.id}`)}
                  openLabel={t('openPending')}
                  statusLabel={t('dueNow')}
                  metaLabel={t('questionsCount', { count: survey.question_count })}
                />
              ))}
            </div>
          )}
        </section>

        {completedSurveys.length > 0 && (
          <section className="space-y-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{t('completedTitle')}</h2>
              <p className="text-sm text-gray-600 dark:text-gray-300">{t('completedDescription')}</p>
            </div>
            <div className="space-y-4">
              {completedSurveys.map((survey) => (
                <div
                  key={survey.id}
                  className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800"
                >
                  <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                    <div className="space-y-2">
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200">
                        <FiCheckCircle size={12} />
                        {t('completed')}
                      </span>
                      <div>
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {localizeSurveyText(survey.title, i18n.language)}
                        </h2>
                        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                          {localizeSurveyText(survey.description, i18n.language)}
                        </p>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {t('lastSubmitted')}: {formatDate(survey.last_submitted_at)}
                        {survey.next_due_at ? ` · ${t('nextDue')}: ${formatDate(survey.next_due_at)}` : ''}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => navigate(`/surveys/${survey.id}`)}
                      className="inline-flex items-center justify-center gap-2 rounded-full border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:border-gray-400 hover:text-gray-900 dark:border-gray-600 dark:text-gray-200 dark:hover:border-gray-500"
                    >
                      {t('review')}
                      <FiArrowRight size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default SurveysPage;
